import React, { useCallback, type Dispatch, type SetStateAction } from 'react';

import { NodeSidePanel } from 'components/node-panel/NodeSidePanel';
import {
  RunWorkflowPanel,
  type RunnerState,
  type StepColorMap,
} from 'components/modals/RunWorkflowModal';
import type { WorkflowEdge, WorkflowEdgeData, WorkflowNode } from 'model/xyflowMapping';

export type SidebarTab = 'editor' | 'runner';

interface OwnProps {
  workflowId: string;
  sidebarTab: SidebarTab;
  selectedNode: WorkflowNode | null;
  allNodes: WorkflowNode[];
  allEdges: WorkflowEdge[];
  runnerState: RunnerState;
  selectedRunnerFile: File | null;
  onSidebarTabChange: (tab: SidebarTab) => void;
  onNodeChange: (updated: WorkflowNode) => void;
  onRenameStep: (oldId: string, newId: string) => void;
  onEdgeDataChange: (edgeId: string, partial: Partial<WorkflowEdgeData>) => void;
  onDeleteEdge: (edgeId: string) => void;
  onAddEdge: (sourceId: string, targetId: string) => void;
  onRunnerStateChange: Dispatch<SetStateAction<RunnerState>>;
  onSelectedRunnerFileChange: (file: File | null) => void;
  onStepColorsChange: (colors: StepColorMap | null) => void;
  onIsRunningChange: (running: boolean) => void;
}

const TABS: readonly SidebarTab[] = ['editor', 'runner'] as const;

/**
 * The right-hand side panel: tabs for switching between the node-editor
 * (inputs/outputs/settings for the selected node) and the run-workflow
 * panel. Owns no state of its own — the parent controls which tab is
 * active.
 */
const EditorSidePanel = ({
  workflowId,
  sidebarTab,
  selectedNode,
  allNodes,
  allEdges,
  runnerState,
  selectedRunnerFile,
  onSidebarTabChange,
  onNodeChange,
  onRenameStep,
  onEdgeDataChange,
  onDeleteEdge,
  onAddEdge,
  onRunnerStateChange,
  onSelectedRunnerFileChange,
  onStepColorsChange,
  onIsRunningChange,
}: OwnProps) => {
  const handleEditorClick = useCallback(() => onSidebarTabChange('editor'), [onSidebarTabChange]);
  const handleRunnerClick = useCallback(() => onSidebarTabChange('runner'), [onSidebarTabChange]);

  return (
    <aside className='side-panel'>
      <div className='tabs' role='tablist'>
        {TABS.map((tab) => (
          <button
            key={tab}
            type='button'
            role='tab'
            aria-selected={sidebarTab === tab}
            onClick={tab === 'editor' ? handleEditorClick : handleRunnerClick}
            className={sidebarTab === tab ? 'active' : ''}
          >
            {tab === 'editor' ? 'Editor' : 'Runner'}
          </button>
        ))}
      </div>
      <div className='body'>
        {sidebarTab === 'editor' ? (
          <NodeSidePanel
            node={selectedNode}
            allNodes={allNodes}
            allEdges={allEdges}
            onNodeChange={onNodeChange}
            onRenameStep={onRenameStep}
            onEdgeDataChange={onEdgeDataChange}
            onDeleteEdge={onDeleteEdge}
            onAddEdge={onAddEdge}
          />
        ) : (
          <RunWorkflowPanel
            workflowId={workflowId}
            state={runnerState}
            onStateChange={onRunnerStateChange}
            selectedFile={selectedRunnerFile}
            onSelectedFileChange={onSelectedRunnerFileChange}
            onStepColorsChange={onStepColorsChange}
            onIsRunningChange={onIsRunningChange}
          />
        )}
      </div>
    </aside>
  );
};

export default React.memo(EditorSidePanel);
